import React from "react";
import { Loader2, Upload, Image as ImageIcon, Trash2, Check } from "lucide-react";
import api from "../../lib/api";

const CATEGORIES = {
  portrait: { label: "Portrait & Branding", color: "#c8821f" },
  maternite: { label: "Maternité & Bébé", color: "#e8488a" },
  celebration: { label: "Célébration", color: "#8a2a2a" },
  location: { label: "Location studio", color: "#2a4e9b" },
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fullUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${BACKEND}${u}`;
  return u;
}

export default function AdminServicesImages() {
  const [services, setServices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [uploadingSlug, setUploadingSlug] = React.useState("");
  const [savedSlug, setSavedSlug] = React.useState("");

  const load = React.useCallback(() => {
    setLoading(true);
    api.get("/services")
      .then(r => setServices(r.data))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const handleUpload = async (slug, file) => {
    if (!file) return;
    setUploadingSlug(slug);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const token = localStorage.getItem("se_admin_token");
      const r = await fetch(`${BACKEND}/api/admin/upload`, {
        method: "POST",
        body: formData,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("upload failed");
      const data = await r.json();
      const image_url = data.url;
      await api.patch(`/admin/services/${slug}`, { image_url });
      setSavedSlug(slug);
      setTimeout(() => setSavedSlug(""), 1800);
      load();
    } catch (e) {
      alert("Échec de l'upload. Vérifie le format (JPG/PNG, max 100 MB).");
    } finally {
      setUploadingSlug("");
    }
  };

  const clearImage = async (slug) => {
    if (!confirm("Supprimer l'image de ce service ?")) return;
    try {
      await api.patch(`/admin/services/${slug}`, { image_url: "" });
      load();
    } catch {
      alert("Erreur suppression.");
    }
  };

  const grouped = React.useMemo(() => {
    const order = ["portrait", "maternite", "celebration", "location"];
    const out = {};
    for (const s of services) {
      const key = s.category || "portrait";
      if (!out[key]) out[key] = [];
      out[key].push(s);
    }
    for (const k of Object.keys(out)) {
      out[k].sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
    }
    const ordered = {};
    for (const k of order) if (out[k]) ordered[k] = out[k];
    return ordered;
  }, [services]);

  return (
    <div className="text-text-cream" data-testid="admin-services-images">
      <div className="mb-10">
        <div className="eyebrow mb-2">Admin</div>
        <h1 className="font-serif text-4xl">Images des services</h1>
        <p className="text-text-cream/50 mt-2 text-sm font-light max-w-2xl">
          Ajoute une photo d'ambiance par service. Elle s'affiche dans la liste accordéon de /reservation
          pour donner envie en un coup d'œil. Format recommandé : JPG carré ou 4:3, au moins 400×400 px.
        </p>
      </div>

      {loading && <div className="text-text-cream/60 text-sm font-light">Chargement…</div>}

      {Object.entries(grouped).map(([cat, items]) => {
        const meta = CATEGORIES[cat] || { label: cat, color: "#c8821f" };
        return (
          <div key={cat} className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-2 w-2 rounded-full" style={{ background: meta.color }} />
              <h2 className="font-serif text-xl">{meta.label}</h2>
              <span className="text-text-cream/40 text-xs font-light">({items.length})</span>
            </div>

            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {items.map(s => {
                const isUploading = uploadingSlug === s.slug;
                const isSaved = savedSlug === s.slug;
                return (
                  <div
                    key={s.slug}
                    className="bg-white/[0.03] border border-white/10 rounded p-4 flex gap-4"
                    data-testid={`service-image-card-${s.slug}`}
                  >
                    <div className="w-24 h-24 shrink-0 rounded overflow-hidden border border-white/10 bg-white/[0.02] flex items-center justify-center">
                      {s.image_url ? (
                        <img
                          src={fullUrl(s.image_url)}
                          alt={s.name}
                          className="w-full h-full object-cover"
                          data-testid={`service-image-preview-${s.slug}`}
                        />
                      ) : (
                        <ImageIcon size={28} className="text-text-cream/30" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col">
                      <div className="font-serif text-base truncate">{s.name}</div>
                      <div className="text-text-cream/40 text-xs font-light mt-0.5">
                        {s.duration_min} min · CHF {s.price_chf}
                      </div>
                      <div className="mt-auto pt-3 flex items-center gap-2 flex-wrap">
                        <label
                          className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded border transition cursor-pointer ${
                            isUploading
                              ? "border-white/10 bg-white/5 text-text-cream/40 cursor-wait"
                              : "border-accent-deep/40 bg-accent-deep/10 text-accent-light hover:bg-accent-deep/20"
                          }`}
                          data-testid={`service-image-upload-${s.slug}`}
                        >
                          {isUploading ? (
                            <><Loader2 size={12} className="animate-spin" /> Upload…</>
                          ) : isSaved ? (
                            <><Check size={12} /> Enregistré</>
                          ) : (
                            <><Upload size={12} /> {s.image_url ? "Remplacer" : "Ajouter"}</>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            disabled={isUploading}
                            onChange={(e) => handleUpload(s.slug, e.target.files?.[0])}
                            className="hidden"
                          />
                        </label>
                        {s.image_url && !isUploading && (
                          <button
                            onClick={() => clearImage(s.slug)}
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded border border-rose-500/30 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 transition"
                            data-testid={`service-image-clear-${s.slug}`}
                          >
                            <Trash2 size={12} /> Supprimer
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
